import {
  Box,
  Button,
  CircularProgress,
  Step,
  StepLabel,
  Stepper,
} from "@mui/material";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { successToster, errorToster } from "../../shared/toster/Toster";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import ApiClient from "../../apis/Api-client";
import {
  EmployeeEducationAndProfessionalInfoSchema,
  EmployeePersonalInfoSchema,
  EmployeeBankInfoSchema,
  EmployeeSalaryInfoSchema,
} from "../../config/forms/employeeFormConfig";
import EmployeePersonalInfoForm from "./EmployeePersonalInfoForm";
import EmployeeProfessionalInfoForm from "./EmployeeProfessionalInfoForm";
import EmployeeBankInfoForm from "./EmployeeBankInfoForm";
import EmployeeSalaryInfoForm from "./EmployeeSalaryInfoForm";
import { END_POINT } from "../../utils/End-points";
import {
  getDefaultValues,
  mapEditDataToFormData,
  transformDataForSubmit,
} from "../../utils/employeeFormMappers";

const EmployeeOnboardingStepper = ({
  formOpenState,
  editData,
  setRefresh,
  setFormOpenState,
  setEditData,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentSchema, setCurrentSchema] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [allCountries, setAllCountries] = useState([]);
  const [sameAsPermanentAdd, setSameAsPermanentAdd] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [proofTypes, setProofTypes] = useState([]);
  const [isFormInteracted, setIsFormInteracted] = useState(false);

  const { api } = ApiClient();
  const activeStepRef = useRef(activeStep);
  activeStepRef.current = activeStep;

  const steps = [
    "Personal",
    "Educational & Professional",
    "Bank Details",
    "Salary Details",
  ];

  const schemas = useMemo(() => ({
    0: EmployeePersonalInfoSchema,
    1: EmployeeEducationAndProfessionalInfoSchema,
    2: EmployeeBankInfoSchema,
    3: EmployeeSalaryInfoSchema,
  }), []);

  const getSchemaForStep = (step) => schemas[step] || yup.object().shape({});

  const {
    handleSubmit,
    trigger,
    getValues,
    register,
    watch,
    setValue,
    formState: { errors, touchedFields, isSubmitted },
    clearErrors,
    reset,
    control,
  } = useForm({
    resolver: (data, context, options) => {
      return yupResolver(getSchemaForStep(activeStepRef.current))(data, context, options);
    },
    context: { proofTypes },
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: useMemo(() => 
      editData ? mapEditDataToFormData(editData, proofTypes) : getDefaultValues()
    , [editData, proofTypes]),
  });

  useEffect(() => {
    setCurrentSchema(getSchemaForStep(activeStep));
  }, [activeStep]);

  useEffect(() => {
    getProofTypes();
    getAllCountries();
  }, []);

  const getProofTypes = async () => {
    try {
      const res = await api.get(END_POINT.UTILS.GET_ALL_PROOF_TYPES);
      if (res?.status === 200) {
        setProofTypes(res.data.data.map(item => ({
          label: item.proof_type,
          value: item.proof_type_id,
        })));
      }
    } catch (error) {
      console.error("error fetching proof types", error);
    }
  };

  const getAllCountries = async () => {
    try {
      const countries = await api.get(END_POINT.UTILS.GET_ALL_COUNTRIES);
      setAllCountries(countries?.data?.data?.map(data => ({
        label: data.country_name,
        value: data.country_id,
        phone_code: data.phone_code || "",
      })));
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  };

  useEffect(() => {
    if (editData?.data?.[0]) {
      const data = editData.data[0];
      reset(mapEditDataToFormData(editData, proofTypes));
      setSelectedImage(data.employee_photo || null);
    } else {
      reset(getDefaultValues());
      setSelectedImage(null);
    }
  }, [editData, reset, proofTypes]);

  const handleNext = async () => {
    const currentStepSchema = getSchemaForStep(activeStep);
    const fieldsToValidate = currentStepSchema?.fields ? Object.keys(currentStepSchema.fields) : [];
    const isValid = await trigger(fieldsToValidate);

    if (isValid) {
      clearErrors();
      setIsFormInteracted(false);
      setActiveStep(prev => prev + 1);
    } else {
      setIsFormInteracted(true);
    }
  };

  const handleBack = () => {
    clearErrors();
    setActiveStep(prev => prev - 1);
  };

  const handleCancel = () => {
    setFormOpenState(false);
    setEditData(null);
    reset(getDefaultValues());
    setActiveStep(0);
  };

  const commonProps = {
    textFieldStyles: {
      label: { sx: { color: "#6F6F6F" } },
      helperText: { sx: { fontSize: { xs: "2.564vw", sm: "1.44vw", md: "0.83vw" } } },
    },
    errors,
    touchedFields,
    isSubmitted: isSubmitted || isFormInteracted,
    register,
    watch,
    setValue,
    setCurrentSchema,
  };

  const stepComponents = {
    0: <EmployeePersonalInfoForm {...commonProps} getValues={getValues} selectedImage={selectedImage} setSelectedImage={setSelectedImage} states={states} setStates={setStates} allCountries={allCountries} setAllCountries={setAllCountries} cities={cities} setCities={setCities} sameAsPermanentAdd={sameAsPermanentAdd} setSameAsPermanentAdd={setSameAsPermanentAdd} proofTypes={proofTypes} />,
    1: <EmployeeProfessionalInfoForm {...commonProps} clearErrors={clearErrors} control={control} />,
    2: <EmployeeBankInfoForm {...commonProps} />,
    3: <EmployeeSalaryInfoForm {...commonProps} getValues={getValues} clearErrors={clearErrors} />,
  };

  const onSubmit = async (data) => {
    if (activeStep < steps.length - 1) {
      handleNext();
      return;
    }
    setIsSubmitting(true);
    try {
      const transformedData = transformDataForSubmit(data, proofTypes);
      const formDataInstance = new FormData();
      formDataInstance.append("employeeData", JSON.stringify(transformedData));

      if (data.employee_photo && typeof data.employee_photo !== 'string') formDataInstance.append("employee_photo", data.employee_photo);
      if (data.id_proof_front && typeof data.id_proof_front !== 'string') formDataInstance.append("id_proof_front", data.id_proof_front);
      if (data.id_proof_back && typeof data.id_proof_back !== 'string') formDataInstance.append("id_proof_back", data.id_proof_back);
      

      const endpoint = editData ? `${END_POINT.EMPLOYEE.UPDATE_EMPLOYEE}/${editData.data[0].employee_key}` : END_POINT.EMPLOYEE.CREATE_EMPLOYEE;
      const method = editData ? api.putFormData : api.postFormData;

      const response = await method(endpoint, formDataInstance, editData ? "Employee updated" : "Employee created");

      if (response) {
        setFormOpenState(false);
        setEditData(null);
        reset(getDefaultValues());
        setActiveStep(0);
        if (setRefresh) setRefresh(prev => !prev);
      }
    } catch (error) {
      console.error("Submission error:", error);
      errorToster("Failed to submit employee data");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box width="100%" height="fit-content" padding="2%" sx={{ display: "flex", flexDirection: "column", boxSizing: "border-box" }}>
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
      </Stepper>

      <form onSubmit={handleSubmit(onSubmit)} onKeyDown={e => e.key === "Enter" && e.target.tagName !== "TEXTAREA" && e.preventDefault()}>
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", marginTop: "5vh", position: "relative" }}>
          {stepComponents[activeStep]}

          <Box sx={{ display: "flex", justifyContent: "center", width: "100%", marginTop: 4, gap: 4 }}>
            {activeStep !== 0 && (
              <Button type="button" onClick={handleBack} variant="contained" sx={{ width: "15%" }} disabled={isSubmitting}>Back</Button>
            )}
            <Button type="button" variant="outlined" onClick={handleCancel} sx={{ width: "15%" }}>Cancel</Button>
            {activeStep < steps.length - 1 ? (
              <Button variant="contained" type="button"  key="next-button" onClick={handleNext} disabled={isSubmitting} sx={{ width: "15%" }}>Next</Button>
            ) : (
              <Button variant="contained" type="submit"  key="submit-button" disabled={isSubmitting} sx={{ width: "15%" }}>
                {isSubmitting ? <CircularProgress size={24} /> : "Submit"}
              </Button>
            )}
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default EmployeeOnboardingStepper;